import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { Button, ListGroup, ListGroupItem, Badge } from 'reactstrap';
import BaggageItemAdderModalForm, {
  BAGGAGE_ITEM_ADDER_FORM,
} from './BaggageItemAdderModalForm';
import {
  getItemsByItineraryId,
  getBaggageItemByCorrelative,
  putUploadItemToItinerary,
} from '../../../../../actions/baggage/Item';

const STATUS = {
  LOADING: 'loading',
  VALID: 'valid',
  WARNING: 'warning',
  INVALID: 'invalid',
  ALREADY_ADDED: 'already_added',
};

let rowIdCounter = 0;
const nextRowId = () => {
  rowIdCounter += 1;
  return `row-${Date.now()}-${rowIdCounter}`;
};

const BaggageItemAdderModal = ({
  itineraryId,
  onCancel,
  onSuccess,
  loading,
  dispatchChange,
  dispatchPutUploadItemToItinerary,
  dispatchGetItemsByItineraryId,
  dispatchGetBaggageItemByCorrelative,
  itemList,
}) => {
  const [scannedList, setScannedList] = useState([]);
  const [closeInProgress, setCloseInProgress] = useState(false);

  const existingIds = new Set(itemList.map((it) => it.id));

  const resetField = () => {
    dispatchChange(BAGGAGE_ITEM_ADDER_FORM, 'correlative', '');
    const input = document.querySelector('.baggage-item-correlative');
    if (input) input.focus();
  };

  const isInOtherItinerary = (itemData) =>
    itemData.itineraryId != null && itemData.itineraryId !== itineraryId;

  const handleScan = ({ correlative }) => {
    if (!correlative) return;

    const trimmed = correlative.trim();
    resetField();

    const pivotId = nextRowId();
    setScannedList((prev) => [
      ...prev,
      {
        rowId: pivotId,
        code: trimmed,
        status: STATUS.LOADING,
        data: null,
        errorMessage: null,
      },
    ]);

    dispatchGetBaggageItemByCorrelative(trimmed)
      .then((items) => {
        const results = Array.isArray(items) ? items : [];

        if (results.length === 0) {
          setScannedList((prev) =>
            prev.map((row) =>
              row.rowId === pivotId
                ? {
                    ...row,
                    status: STATUS.INVALID,
                    errorMessage: 'Equipaje no encontrado',
                  }
                : row,
            ),
          );
          return;
        }

        setScannedList((prev) => {
          const idx = prev.findIndex((row) => row.rowId === pivotId);
          if (idx === -1) {
            return prev;
          }

          const sessionIds = new Set(
            prev
              .filter(
                (row) =>
                  row.rowId !== pivotId &&
                  (row.status === STATUS.VALID ||
                    row.status === STATUS.WARNING) &&
                  row.data,
              )
              .map((row) => row.data.id),
          );

          const resolvedRows = results.map((itemData) => {
            let status;
            if (existingIds.has(itemData.id) || sessionIds.has(itemData.id)) {
              status = STATUS.ALREADY_ADDED;
            } else if (isInOtherItinerary(itemData)) {
              status = STATUS.WARNING;
            } else {
              status = STATUS.VALID;
            }

            return {
              rowId: nextRowId(),
              code: trimmed,
              status,
              data: itemData,
              errorMessage: null,
            };
          });

          return [
            ...prev.slice(0, idx),
            ...resolvedRows,
            ...prev.slice(idx + 1),
          ];
        });
      })
      .catch((error) => {
        setScannedList((prev) =>
          prev.map((row) =>
            row.rowId === pivotId
              ? {
                  ...row,
                  status: STATUS.INVALID,
                  errorMessage: error.message || 'Equipaje no encontrado',
                }
              : row,
          ),
        );
      });
  };

  const handleRemove = (rowId) => {
    setScannedList((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const handleConfirm = async () => {
    setCloseInProgress(true);

    const itemsToSubmit = scannedList.filter(
      (row) => row.status === STATUS.VALID || row.status === STATUS.WARNING,
    );

    if (itemsToSubmit.length === 0) return;

    const response = await dispatchPutUploadItemToItinerary({
      itineraryId,
      itemIds: itemsToSubmit.map((row) => row.data.id),
    });

    if (!response) return;

    await dispatchGetItemsByItineraryId({ itineraryId });

    onSuccess();

    setCloseInProgress(false);
  };

  const renderStatusBadge = (row) => {
    if (row.status === STATUS.LOADING) {
      return (
        <Badge color="secondary">
          <i className="fa fa-spinner fa-spin" /> Verificando...
        </Badge>
      );
    }
    if (row.status === STATUS.VALID) {
      return <Badge color="success">Válido</Badge>;
    }
    if (row.status === STATUS.WARNING) {
      return (
        <Badge
          color="warning"
          title={`Pertenece al itinerario ${row.data.itineraryId}`}
        >
          En otro itinerario - ({row.data.itineraryId})
        </Badge>
      );
    }
    if (row.status === STATUS.ALREADY_ADDED) {
      return <Badge color="info">Ya en manifiesto</Badge>;
    }
    return (
      <Badge color="danger" title={row.errorMessage}>
        {row.errorMessage}
      </Badge>
    );
  };

  const renderRowColor = (status) => {
    if (status === STATUS.VALID) return 'success';
    if (status === STATUS.WARNING) return 'warning';
    if (status === STATUS.INVALID) return 'danger';
    if (status === STATUS.ALREADY_ADDED) return 'info';
    return '';
  };

  const submittableCount = scannedList.filter(
    (row) => row.status === STATUS.VALID || row.status === STATUS.WARNING,
  ).length;

  const hasLoading = scannedList.some((row) => row.status === STATUS.LOADING);

  return (
    <div>
      <BaggageItemAdderModalForm onSubmit={handleScan} />

      {scannedList.length > 0 && (
        <div className="mt-3">
          <h6>Equipajes escaneados ({scannedList.length})</h6>
          <ListGroup>
            {scannedList.map((row) => (
              <ListGroupItem
                key={row.rowId}
                className="d-flex justify-content-between align-items-center"
                color={renderRowColor(row.status)}
              >
                <strong>
                  {row.code} - {row.data ? row.data.description : ''}
                </strong>
                <div className="d-flex align-items-center">
                  {renderStatusBadge(row)}
                  <Button
                    type="button"
                    color="link"
                    size="sm"
                    className="text-danger p-0 ml-2"
                    onClick={() => handleRemove(row.rowId)}
                  >
                    <i className="fa fa-times" />
                  </Button>
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        </div>
      )}

      <div className="mt-3 d-flex justify-content-end">
        <Button
          type="button"
          color="secondary"
          outline
          onClick={onCancel}
          className="mr-2"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          color="primary"
          disabled={
            submittableCount === 0 || hasLoading || loading || closeInProgress
          }
          onClick={handleConfirm}
        >
          Agregar {submittableCount > 0 ? `(${submittableCount})` : ''} al
          manifiesto
        </Button>
      </div>
    </div>
  );
};

BaggageItemAdderModal.propTypes = {
  itineraryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  dispatchPutUploadItemToItinerary: PropTypes.func.isRequired,
  dispatchGetItemsByItineraryId: PropTypes.func.isRequired,
  dispatchGetBaggageItemByCorrelative: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  itemList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

BaggageItemAdderModal.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ BaggageUnit }) => ({
  loading: !BaggageUnit.Item.getIn(['current', 'activity']).isEmpty(),
  itemList: BaggageUnit.Item.getIn(['content', 'content', 'content']) || [],
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchPutUploadItemToItinerary: putUploadItemToItinerary,
  dispatchGetItemsByItineraryId: getItemsByItineraryId,
  dispatchGetBaggageItemByCorrelative: getBaggageItemByCorrelative,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaggageItemAdderModal);
